import React, { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, PhoneOff, Video, X, MicOff, Mic, VideoOff, ShieldAlert, ExternalLink } from "lucide-react";
import { useCall } from "@/context/CallContext";

function formatDuration(startMs) {
  const s = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/**
 * Detect the browser family + platform. Used ONLY to show accurate
 * "how to re-enable mic/camera" instructions when the browser has blocked
 * a permission — the JS layer cannot programmatically re-prompt once denied,
 * so the user must take action from browser settings.
 */
function detectBrowser() {
  if (typeof navigator === "undefined") return { name: "unknown", isMobile: false };
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid;
  let name = "chrome";
  if (isIOS) name = "safari-ios";
  else if (/Firefox\//.test(ua)) name = "firefox";
  else if (/Edg\//.test(ua)) name = "edge";
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) name = "safari";
  else if (/Chrome\//.test(ua)) name = isAndroid ? "chrome-android" : "chrome";
  return { name, isMobile, isIOS, isAndroid };
}

/** Return step-by-step instructions in Turkish for the detected browser. */
function permissionInstructions({ name, isIOS, isAndroid }, needsVideo) {
  const perm = needsVideo ? "kamera ve mikrofon" : "mikrofon";
  const steps = [];
  if (isIOS) {
    steps.push(`iPhone Ayarları → Safari → ${needsVideo ? "Kamera / Mikrofon" : "Mikrofon"} → "İzin Ver" seç`);
    steps.push("Safari'yi tamamen kapat ve yeniden aç");
    steps.push("Sonra bu sayfaya dönüp aramayı tekrar başlat");
  } else if (isAndroid) {
    steps.push("Adres çubuğundaki 🔒 kilit simgesine bas");
    steps.push(`Site ayarları → ${perm} → "İzin ver" olarak değiştir`);
    steps.push("Sayfayı yenile ve aramayı tekrar başlat");
  } else if (name === "firefox") {
    steps.push("Adres çubuğunun solundaki kalkan/kilit simgesine tıkla");
    steps.push(`"Bağlantı güvenli" → "İzinler" → ${perm} → yasağı kaldır`);
    steps.push("Sayfayı yenile ve aramayı tekrar başlat");
  } else {
    // Chrome, Edge, generic
    steps.push("Adres çubuğunun solundaki 🔒 simgesine tıkla");
    steps.push(`"Site ayarları" → ${perm} → "İzin ver" seç`);
    steps.push("Sayfayı yenile ve aramayı tekrar başlat");
  }
  return steps;
}

const Initial = ({ name }) => (
  <span className="font-display text-4xl md:text-5xl">
    {((name || "?")[0] || "?").toUpperCase()}
  </span>
);

/**
 * Full-screen overlay for incoming/outgoing/active calls. Renders nothing
 * when state === "idle".
 */
export default function CallOverlay() {
  const {
    state, callType, peer, muted, cameraOff, connectedAt, error, lastEndedInfo,
    localStream, remoteStream,
    acceptCall, rejectCall, endCall, toggleMute, toggleCamera, clearEndedToast,
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [, tick] = React.useReducer((x) => x + 1, 0);
  const browser = useMemo(detectBrowser, []);
  const isPermDenied = lastEndedInfo?.status === "permission_denied";

  // Attach streams to video elements when refs available
  useEffect(() => {
    const el = localVideoRef.current;
    if (el && localStream?.current) {
      el.srcObject = localStream.current;
      el.play?.().catch(() => {});
    }
  }, [state, callType, localStream, cameraOff]);

  useEffect(() => {
    const el = remoteVideoRef.current;
    if (el && remoteStream?.current) {
      el.srcObject = remoteStream.current;
      el.play?.().catch(() => {});
    }
  }, [state, remoteStream]);

  // 1-sec tick to redraw timer while connected
  useEffect(() => {
    if (state !== "connected" || !connectedAt) return;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [state, connectedAt]);

  const showOverlay = state !== "idle";

  return (
    <>
      {/* Permission-denied modal — persistent, actionable, browser-specific.
          The user cannot be re-prompted programmatically once they've denied;
          they must re-enable via browser settings, so we walk them through it. */}
      <AnimatePresence>
        {isPermDenied && (
          <motion.div
            key="perm-denied"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2147481500] bg-black/60 flex items-center justify-center p-4"
            data-testid="call-permission-modal"
            role="dialog"
            aria-modal="true"
          >
            <motion.div
              initial={{ scale: 0.94, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 12 }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              className="w-full max-w-md bg-white text-[#0A0A0A] border-hard shadow-hard rounded-3xl p-5 md:p-6"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-[#FFD0C2] border-hard-2 flex items-center justify-center">
                  <ShieldAlert strokeWidth={3} className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase font-black tracking-widest text-[#555]">Mikrofon/Kamera izni</div>
                  <div className="font-display text-xl md:text-2xl leading-tight mt-0.5">Tarayıcı erişimi engelledi</div>
                  <p className="text-sm text-[#333] mt-2 leading-snug">
                    Tarayıcı, {lastEndedInfo?.needsVideo ? "kamera ve mikrofon" : "mikrofon"} erişimini bir önceki denemede reddettiğin için tekrar soramıyor. Aşağıdaki adımlarla tarayıcı ayarlarından tekrar açman gerekiyor:
                  </p>
                </div>
                <button
                  onClick={clearEndedToast}
                  data-testid="call-permission-close"
                  className="w-8 h-8 shrink-0 border-hard-2 rounded-lg bg-white flex items-center justify-center press-btn"
                  aria-label="Kapat"
                >
                  <X strokeWidth={3} className="w-3.5 h-3.5" />
                </button>
              </div>

              <ol className="mt-4 space-y-2" data-testid="call-permission-steps">
                {permissionInstructions(browser, !!lastEndedInfo?.needsVideo).map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-6 h-6 shrink-0 rounded-full bg-[#CCFF00] border-hard-2 text-xs font-black flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="text-sm leading-snug">{step}</span>
                  </li>
                ))}
              </ol>

              <div className="mt-5 flex flex-col md:flex-row gap-2">
                <button
                  onClick={() => window.location.reload()}
                  data-testid="call-permission-reload"
                  className="flex-1 bg-[#0A0A0A] text-white font-black rounded-xl px-4 py-2.5 border-hard shadow-hard press-btn flex items-center justify-center gap-2"
                >
                  <ExternalLink strokeWidth={3} className="w-4 h-4" />
                  Sayfayı yenile
                </button>
                <button
                  onClick={clearEndedToast}
                  data-testid="call-permission-dismiss"
                  className="flex-1 bg-white border-hard-2 font-black rounded-xl px-4 py-2.5 press-btn shadow-hard-sm"
                >
                  Kapat
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Post-call toast (bottom-center, 5s) — skipped when the permission modal is showing */}
      <AnimatePresence>
        {lastEndedInfo && !isPermDenied && (
          <motion.div
            key="ended-toast"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed z-[2147480500] left-1/2 -translate-x-1/2 bottom-[110px] md:bottom-6 bg-white border-hard shadow-hard rounded-2xl px-4 py-2.5 flex items-center gap-3"
            data-testid="call-ended-toast"
            role="status"
          >
            <div className="w-8 h-8 rounded-lg border-hard-2 bg-[#FFD0C2] flex items-center justify-center">
              <PhoneOff strokeWidth={3} className="w-4 h-4" />
            </div>
            <div className="text-sm font-bold">
              {lastEndedInfo.status === "no_answer" && `Cevap yok — ${lastEndedInfo.peer?.name || ""}`}
              {lastEndedInfo.status === "rejected" && "Arama reddedildi"}
              {lastEndedInfo.status === "rejected_by_peer" && `${lastEndedInfo.peer?.name || "Karşı taraf"} reddetti`}
              {lastEndedInfo.status === "canceled_by_peer" && "Arayan iptal etti"}
              {lastEndedInfo.status === "ended" && "Görüşme sona erdi"}
              {lastEndedInfo.status === "peer_left" && "Karşı taraf ayrıldı"}
              {lastEndedInfo.status === "busy" && "Karşı taraf meşgul"}
              {lastEndedInfo.status === "hangup" && "Görüşme sona erdi"}
              {lastEndedInfo.status === "failed" && "Bağlantı kurulamadı"}
              {lastEndedInfo.status === "media_failed" && (lastEndedInfo.message || "Kamera/mikrofon açılamadı")}
              {lastEndedInfo.status === "network" && "Bağlantı düştü"}
            </div>
            <button
              onClick={clearEndedToast}
              className="w-6 h-6 border-hard-2 rounded-md bg-white flex items-center justify-center"
              aria-label="Kapat"
              data-testid="call-ended-toast-close"
            >
              <X strokeWidth={3} className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showOverlay && (
          <motion.div
            key={`call-overlay-${state}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2147481000] bg-[#0A0A0A] text-white flex flex-col overflow-hidden"
            data-testid={`call-overlay-${state}`}
          >
            {/* Remote video (video calls, connected state) */}
            {callType === "video" && state === "connected" && (
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="call-remote-video"
              />
            )}

            {/* Dark backdrop w/ big avatar (voice call OR pre-connected) */}
            {(callType === "audio" || state !== "connected") && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-white/40 bg-[#CCFF00] text-[#0A0A0A] flex items-center justify-center overflow-hidden shadow-hard-lg">
                  {peer?.picture
                    ? <img src={peer.picture} alt="" className="w-full h-full object-cover" />
                    : <Initial name={peer?.name} />}
                </div>
              </div>
            )}

            {/* Local preview (video calls, connected state) */}
            {callType === "video" && state === "connected" && (
              <div className="absolute top-4 right-4 w-28 h-40 md:w-36 md:h-52 rounded-2xl overflow-hidden border-hard-2 shadow-hard bg-[#111] z-10">
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover transform scale-x-[-1]"
                  data-testid="call-local-video"
                />
                {cameraOff && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <VideoOff strokeWidth={3} className="w-6 h-6" />
                  </div>
                )}
              </div>
            )}

            {/* Header info */}
            <div
              className="relative z-10 text-center pt-8"
              style={{ paddingTop: "calc(2rem + env(safe-area-inset-top, 0px))" }}
            >
              <div className="text-[11px] uppercase font-black tracking-widest text-white/70" data-testid="call-status">
                {state === "outgoing" && "Aranıyor…"}
                {state === "incoming" && (callType === "video" ? "Görüntülü arama" : "Sesli arama")}
                {state === "connected" && (callType === "video" ? "GÖRÜNTÜLÜ" : "SESLİ") + " · " + formatDuration(connectedAt || Date.now())}
              </div>
              <div className="font-display text-3xl md:text-4xl mt-1" data-testid="call-peer-name">
                {peer?.name}
              </div>
              {error && (
                <div className="mt-3 mx-auto max-w-sm bg-white text-[#0A0A0A] border-hard-2 rounded-xl px-3 py-2 text-sm font-bold" data-testid="call-error">
                  {error}
                </div>
              )}
            </div>

            {/* Controls */}
            <div
              className="relative z-10 mt-auto pb-8 flex items-center justify-center gap-4 md:gap-6"
              style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
            >
              {state === "incoming" ? (
                <>
                  <button
                    onClick={rejectCall}
                    data-testid="call-reject-btn"
                    className="w-16 h-16 rounded-full bg-[#FF3B30] border-hard shadow-hard flex items-center justify-center press-btn"
                    aria-label="Reddet"
                  >
                    <PhoneOff strokeWidth={3} className="w-6 h-6" />
                  </button>
                  <button
                    onClick={acceptCall}
                    data-testid="call-accept-btn"
                    className="w-16 h-16 rounded-full bg-[#CCFF00] text-[#0A0A0A] border-hard shadow-hard flex items-center justify-center press-btn animate-pulse"
                    aria-label="Kabul et"
                  >
                    {callType === "video" ? <Video strokeWidth={3} className="w-6 h-6" /> : <Phone strokeWidth={3} className="w-6 h-6" />}
                  </button>
                </>
              ) : (
                <>
                  {/* Mute */}
                  <button
                    onClick={toggleMute}
                    data-testid="call-mute-btn"
                    className={`w-14 h-14 rounded-full border-hard shadow-hard flex items-center justify-center press-btn ${muted ? "bg-white text-[#0A0A0A]" : "bg-white/15 backdrop-blur border-white/40"}`}
                    aria-label={muted ? "Sesi aç" : "Sessize al"}
                  >
                    {muted ? <MicOff strokeWidth={3} className="w-5 h-5" /> : <Mic strokeWidth={3} className="w-5 h-5" />}
                  </button>

                  {/* Hang up */}
                  <button
                    onClick={() => endCall("hangup")}
                    data-testid="call-hangup-btn"
                    className="w-16 h-16 rounded-full bg-[#FF3B30] border-hard shadow-hard flex items-center justify-center press-btn"
                    aria-label="Kapat"
                  >
                    <PhoneOff strokeWidth={3} className="w-6 h-6" />
                  </button>

                  {/* Camera toggle */}
                  {callType === "video" && (
                    <button
                      onClick={toggleCamera}
                      data-testid="call-camera-btn"
                      className={`w-14 h-14 rounded-full border-hard shadow-hard flex items-center justify-center press-btn ${cameraOff ? "bg-white text-[#0A0A0A]" : "bg-white/15 backdrop-blur border-white/40"}`}
                      aria-label={cameraOff ? "Kamerayı aç" : "Kamerayı kapat"}
                    >
                      {cameraOff ? <VideoOff strokeWidth={3} className="w-5 h-5" /> : <Video strokeWidth={3} className="w-5 h-5" />}
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
